import { z } from 'zod'
import { decimalSchema, uuidSchema } from './common.ts'
import { warehouseUnitCodeSchema } from './units.ts'
import { positiveStockQuantitySchema } from './warehouse.ts'

export const bomAvailabilityItemInputSchema = z.object({
  materialId: uuidSchema,
  quantity: positiveStockQuantitySchema,
  unit: warehouseUnitCodeSchema,
  wasteFactor: positiveStockQuantitySchema.default('1'),
  operation: z.string().trim().min(1).max(200).nullable().optional().default(null),
})

export const checkBomAvailabilityInputSchema = z.object({
  orderId: uuidSchema.optional(),
  sourceBomVersion: z.string().trim().min(1).max(100).optional(),
  items: z.array(bomAvailabilityItemInputSchema).min(1).max(500),
})

export const reserveProductionSupplyInputSchema = checkBomAvailabilityInputSchema.extend({
  orderId: uuidSchema,
  sourceBomVersion: z.string().trim().min(1).max(100),
})

export const productionSupplyOrderInputSchema = z.object({ orderId: uuidSchema })

export const productionSupplyStatusSchema = z.enum(['preview', 'active', 'consumed', 'released'])

export const productionSupplySourceLineSchema = z.object({
  enteredQuantity: decimalSchema,
  enteredUnit: warehouseUnitCodeSchema,
  conversionFactor: decimalSchema,
  baseQuantity: decimalSchema,
  wasteFactor: decimalSchema,
  requiredQuantity: decimalSchema,
  operation: z.string().nullable(),
  productionOrderItemId: uuidSchema.optional(),
  productionOrderItemQuantity: decimalSchema.optional(),
  perUnitRequiredQuantity: decimalSchema.optional(),
})

export const productionSupplyItemSchema = z.object({
  materialId: uuidSchema,
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  baseUnit: warehouseUnitCodeSchema,
  required: decimalSchema,
  physical: decimalSchema,
  reserved: decimalSchema,
  reservedForOrder: decimalSchema,
  available: decimalSchema,
  ordered: decimalSchema,
  shortage: decimalSchema,
  toOrder: decimalSchema,
  sourceLines: z.array(productionSupplySourceLineSchema),
})

export const productionSupplyReportSchema = z.object({
  orderId: uuidSchema.nullable(),
  sourceBomVersion: z.string().nullable(),
  status: productionSupplyStatusSchema,
  hasShortage: z.boolean(),
  items: z.array(productionSupplyItemSchema),
})

export const productionReadinessStateSchema = z.enum([
  'not_applicable',
  'unlinked',
  'missing_bom',
  'shortage',
  'ready',
  'consumed',
  'released',
])

export const productionOrderItemReadinessSchema = z.object({
  orderItemId: uuidSchema,
  position: z.number().int().positive(),
  description: z.string(),
  quantity: decimalSchema.nullable(),
  unitSymbol: z.string().nullable(),
  itemKind: z.enum(['product', 'service']).nullable(),
  engineeringProductId: uuidSchema.nullable(),
  state: productionReadinessStateSchema,
  sourceBomVersion: z.string().nullable(),
  materials: z.array(productionSupplyItemSchema),
})

export const productionOrderReadinessSchema = z.object({
  orderId: uuidSchema,
  state: productionReadinessStateSchema,
  canComplete: z.boolean(),
  items: z.array(productionOrderItemReadinessSchema),
})

export const productionOrderReadinessSummarySchema = productionOrderReadinessSchema.pick({
  orderId: true,
  state: true,
  canComplete: true,
})

export type BomAvailabilityItemInput = z.infer<typeof bomAvailabilityItemInputSchema>
export type CheckBomAvailabilityInput = z.infer<typeof checkBomAvailabilityInputSchema>
export type ReserveProductionSupplyInput = z.infer<typeof reserveProductionSupplyInputSchema>
export type ProductionSupplyReport = z.infer<typeof productionSupplyReportSchema>
export type ProductionReadinessState = z.infer<typeof productionReadinessStateSchema>
export type ProductionOrderItemReadiness = z.infer<typeof productionOrderItemReadinessSchema>
export type ProductionOrderReadiness = z.infer<typeof productionOrderReadinessSchema>
export type ProductionOrderReadinessSummary = z.infer<typeof productionOrderReadinessSummarySchema>
